import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import MenuBookPage from "@/components/dinein/MenuBookPage";
import DineInDishCard from "@/components/dinein/DineInDishCard";
import { dineInMenuData } from "@/data/dineInMenuData";

interface DineInMenuDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DineInMenuDialog = ({ open, onOpenChange }: DineInMenuDialogProps) => {
  const [currentSpread, setCurrentSpread] = useState(0);
  const [currentMobilePage, setCurrentMobilePage] = useState<'left' | 'right'>('left');

  // Define all pages as spreads (two pages at a time)
  const pages = [
    // Cover Page (spread 0)
    {
      left: (
        <div className="h-full flex flex-col items-center justify-center text-center p-12">
          <div className="space-y-8">
            <div className="relative">
              <h1 className="text-6xl md:text-7xl font-display font-black text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent drop-shadow-2xl tracking-wider">
                MENY
              </h1>
              <div className="absolute -inset-4 bg-gold/10 blur-3xl -z-10 rounded-full"></div>
            </div>
            <div className="space-y-3">
              <p className="text-3xl font-elegant text-gold-light italic">Currylicious Yum Yum</p>
              <div className="flex items-center justify-center gap-3">
                <div className="h-px w-8 bg-gradient-to-r from-transparent to-gold/50"></div>
                <p className="text-base text-muted-foreground uppercase tracking-[0.3em] font-sans font-light">Indian · Thai · Sushi</p>
                <div className="h-px w-8 bg-gradient-to-l from-transparent to-gold/50"></div>
              </div>
              <p className="text-sm text-muted-foreground/70 mt-6 font-sans tracking-widest">RESTAURANG</p>
            </div>
            <div className="relative w-32 h-px mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 rounded-full bg-gold shadow-gold-glow"></div>
            </div>
            <p className="text-xs text-gold/50 mt-10 font-sans tracking-widest">www.currylicious.se</p>
          </div>
        </div>
      ),
      right: (
        <div className="h-full flex flex-col justify-center p-10 space-y-10">
          <div className="text-center space-y-8">
            <div className="relative inline-block">
              <h2 className="text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.2em]">
                INDISKA
              </h2>
              <div className="absolute -inset-2 bg-gold/10 blur-2xl -z-10 rounded-full"></div>
              <div className="absolute -bottom-3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
            <div className="space-y-4 mt-8">
              <p className="text-base text-foreground/90 leading-relaxed font-sans">
                Samtliga huvudrätter serveras med ris garnerat med rostad lök, om inte annat anges.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                Vid allergier, vänligen rådfråga personalen angående innehållet i våra rätter.
              </p>
            </div>
          </div>
        </div>
      )
    },
    // Spread 1: Indiska Förrätter + Kockens Rekommendation
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                FÖRRÄTTER
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          {dineInMenuData.indiskaForratter.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                KOCKENS REKOMMENDATION
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Observera: Samtliga rätter innehåller koriander, vitlök och ingefära.
          </p>
          {dineInMenuData.kockensRekommendation.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      )
    },
    // Spread 2: Balti + Tandoori & Grillat
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                BALTI
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Dessa rätter kan tillagas med färsk mango, endast mot en extra kostnad av 10 kr per rätt.
          </p>
          {dineInMenuData.balti.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                TANDOORI & GRILLAT
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Tandoorirätter serveras med stekta säsongensgrönsaker samt currysås.
          </p>
          {dineInMenuData.tandooriGrillat.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      )
    },
    // Spread 3: Vegetariskt + Tandoor & Tillbehör
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                VEGETARISKT
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Observera: Samtliga rätter innehåller koriander, vitlök och ingefära.
          </p>
          {dineInMenuData.vegetariskt.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                TANDOOR
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          {dineInMenuData.naan.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
          <div className="mt-10">
            <div className="text-center mb-8">
              <div className="relative inline-block">
                <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                  TILLBEHÖR
                </h2>
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
              </div>
            </div>
            {dineInMenuData.indiskaTillbehor.map((dish) => (
              <DineInDishCard key={dish.number} {...dish} />
            ))}
          </div>
        </div>
      )
    },
    // Spread 4: Thai Introduction + Thai Förrätter
    {
      left: (
        <div className="h-full flex flex-col justify-center p-10 space-y-10">
          <div className="text-center space-y-8">
            <div className="relative inline-block">
              <h2 className="text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.2em]">
                THAI
              </h2>
              <div className="absolute -inset-2 bg-gold/10 blur-2xl -z-10 rounded-full"></div>
              <div className="absolute -bottom-3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
            <div className="space-y-4 mt-8">
              <p className="text-base text-foreground/90 leading-relaxed font-sans">
                Samtliga huvudrätter serveras med ris om inte annat anges.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                Vid allergier, vänligen rådfråga personalen angående innehållet i våra rätter.
              </p>
            </div>
          </div>
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                FÖRRÄTTER
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Alla Förrätter kan serveras med Ris eller Nudlar mot en extra kostnad på 35kr per rätt.
          </p>
          {dineInMenuData.thaiForratter.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      )
    },
    // Spread 5: Thai Huvudrätter + Sushi Introduction
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                KOCKENS REKOMMENDATION
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          {dineInMenuData.thaiHuvratter.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      ),
      right: (
        <div className="h-full flex flex-col justify-center p-10">
          <div className="text-center space-y-8">
            <div className="relative inline-block">
              <h2 className="text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.2em]">
                SUSHI
              </h2>
              <div className="absolute -inset-2 bg-gold/10 blur-2xl -z-10 rounded-full"></div>
              <div className="absolute -bottom-3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
            <div className="space-y-4 mt-8">
              <p className="text-base text-foreground/90 leading-relaxed font-sans">
                Obs! Alla sushirätter serveras med wasabi, ingefära och wakami.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                Vid allergier, vänligen rådfråga personalen angående innehållet i våra rätter.
              </p>
            </div>
          </div>
        </div>
      )
    },
    // Spread 6: Sushi Hosomaki + Uramaki
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                HOSOMAKI 8 BITAR
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          {dineInMenuData.sushiHosomaki.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                URAMAKI 8 BITAR
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          {dineInMenuData.sushiUramaki.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
        </div>
      )
    },
    // Spread 7: Sushi Nigiri & Tillbehör + Barnmeny
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                NIGIRI 2 BITAR
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          {dineInMenuData.sushiNigiri.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
          <div className="mt-10">
            <div className="text-center mb-8">
              <div className="relative inline-block">
                <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                  TILLBEHÖR
                </h2>
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
              </div>
            </div>
            {dineInMenuData.sushiTillbehor.map((dish, index) => (
              <DineInDishCard key={index} {...dish} />
            ))}
          </div>
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                BARN & BURGER MENY
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            För våra yngsta gäster erbjuder vi en särskild barnmeny med reducerade portionsstorlekar.
          </p>
          <h3 className="text-xl font-elegant font-semibold text-gold mb-6 text-center">BARN</h3>
          {dineInMenuData.barnmeny.map((dish) => (
            <DineInDishCard key={dish.number} {...dish} />
          ))}
          <div className="mt-8">
            <h3 className="text-xl font-elegant font-semibold text-gold mb-6 text-center">BURGER</h3>
            {dineInMenuData.burgers.map((dish) => (
              <DineInDishCard key={dish.number} {...dish} />
            ))}
          </div>
        </div>
      )
    },
    // Spread 8: Dessert + Mocktails & Beverages
    {
      left: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                DESSERT
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Vårt dessertutbud erbjuder något för alla smaker, från klassiska favoriter till spännande nyheter.
          </p>
          {dineInMenuData.dessert.map((dish, index) => (
            <DineInDishCard key={index} {...dish} />
          ))}
        </div>
      ),
      right: (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <h2 className="text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-[0.15em] mb-4">
                MOCKTAILS & BEVERAGES
              </h2>
              <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold/60 to-transparent"></div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-6 italic text-center">
            Mocktails är populära alternativ för dem som vill njuta av festliga drycker utan alkohol.
          </p>
          <h3 className="text-xl font-elegant font-semibold text-gold mb-6 text-center">DRYCKER</h3>
          {dineInMenuData.mocktails.map((drink, index) => (
            <DineInDishCard key={index} {...drink} />
          ))}
          <div className="mt-8">
            {dineInMenuData.beverages.map((drink, index) => (
              <DineInDishCard key={index} {...drink} />
            ))}
          </div>
        </div>
      )
    },
    // Spread 9: Back Cover
    {
      left: (
        <div className="h-full flex flex-col items-center justify-center text-center p-12">
          <div className="space-y-8">
            <div className="relative">
              <h2 className="text-5xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-br from-gold via-gold-light to-gold-accent tracking-wider">
                Tack!
              </h2>
              <div className="absolute -inset-4 bg-gold/10 blur-3xl -z-10 rounded-full"></div>
            </div>
            <p className="text-xl text-foreground/80 font-sans">Vi ser fram emot att välkomna dig</p>
            <div className="relative w-32 h-px mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
              <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 rounded-full bg-gold shadow-gold-glow"></div>
            </div>
            <div className="space-y-3 text-foreground/70 mt-8">
              <p className="font-elegant text-gold text-2xl">Currylicious Yum Yum</p>
              <p className="text-sm uppercase tracking-[0.3em] font-sans">RESTAURANG</p>
              <p className="text-gold/50 mt-6 text-sm tracking-widest font-sans">www.currylicious.se</p>
            </div>
          </div>
        </div>
      ),
      right: (
        <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-gradient-to-br from-background/50 via-background/30 to-background/50 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-gold blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-40 h-40 rounded-full bg-gold blur-3xl"></div>
          </div>
          
          <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-2xl min-h-[400px]">
            {/* Contact Information */}
            <div className="space-y-8 w-full max-w-md">
              {/* Phone */}
              <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-background/10 backdrop-blur-sm hover:bg-background/20 transition-all duration-300">
                <span className="text-3xl">📞</span>
                <p className="text-xl text-foreground font-sans tracking-wide">040 47 00 60</p>
              </div>

              {/* Email */}
              <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-background/10 backdrop-blur-sm hover:bg-background/20 transition-all duration-300">
                <span className="text-3xl">📧</span>
                <p className="text-xl text-foreground font-sans">curryskanor@gmail.com</p>
              </div>

              {/* Address */}
              <div className="flex items-center justify-center gap-4 p-4 rounded-xl bg-background/10 backdrop-blur-sm hover:bg-background/20 transition-all duration-300">
                <span className="text-3xl">📍</span>
                <p className="text-xl text-foreground font-sans text-center">Falsterbovägen 21, 239 33 Skanör</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const totalSpreads = pages.length;

  const nextSpread = () => {
    if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(currentSpread + 1);
      setCurrentMobilePage('left');
    }
  };

  const prevSpread = () => {
    if (currentSpread > 0) {
      setCurrentSpread(currentSpread - 1);
      setCurrentMobilePage('left');
    }
  };

  const nextMobilePage = () => {
    if (currentMobilePage === 'left') {
      setCurrentMobilePage('right');
    } else if (currentSpread < totalSpreads - 1) {
      setCurrentSpread(currentSpread + 1);
      setCurrentMobilePage('left');
    }
  };

  const prevMobilePage = () => {
    if (currentMobilePage === 'right') {
      setCurrentMobilePage('left');
    } else if (currentSpread > 0) {
      setCurrentSpread(currentSpread - 1);
      setCurrentMobilePage('right');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[98vw] sm:max-w-[95vw] xl:max-w-[1600px] h-[95vh] p-0 bg-gradient-to-br from-background/98 via-luxury-dark/95 to-background/98 backdrop-blur-2xl border-2 border-gold/30 overflow-hidden flex flex-col shadow-[0_0_100px_rgba(0,0,0,0.8)]">
        {/* Ambient glow effect */}
        <div className="absolute inset-0 bg-gradient-radial from-gold/5 via-transparent to-transparent pointer-events-none"></div>
        
        <div className="relative h-full flex flex-col">

          {/* Book Container */}
          <div className="flex-1 flex items-center justify-center p-2 sm:p-4 overflow-hidden relative">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 opacity-[0.02]" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--gold)) 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
            
            <div className="relative w-full h-full max-w-6xl flex flex-col lg:flex-row gap-2 sm:gap-4 perspective-1000">
              {/* Mobile/Tablet - Single Page View */}
              <div className="flex lg:hidden relative flex-1 pointer-events-none h-full">
                <MenuBookPage pageNumber={currentSpread * 2 + (currentMobilePage === 'left' ? 1 : 2)}>
                  {pages[currentSpread][currentMobilePage]}
                </MenuBookPage>
              </div>

              {/* Left Page - Desktop only */}
              <div className="hidden lg:flex relative flex-1 pointer-events-none h-full">
                <MenuBookPage isLeftPage pageNumber={currentSpread * 2 + 1}>
                  {pages[currentSpread].left}
                </MenuBookPage>
              </div>

              {/* Center Navigation Area - Desktop only */}
              <div className="hidden lg:flex flex-col items-center justify-center gap-8 z-50 h-full relative">
                {/* Decorative top ornament */}
                <div className="w-6 h-6 opacity-30">
                  <svg viewBox="0 0 100 100" className="text-gold">
                    <circle cx="50" cy="50" r="3" fill="currentColor"/>
                    <circle cx="50" cy="30" r="2" fill="currentColor" opacity="0.6"/>
                    <circle cx="50" cy="70" r="2" fill="currentColor" opacity="0.6"/>
                  </svg>
                </div>
                
                {/* Previous Page Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={prevSpread}
                  disabled={currentSpread === 0}
                  className="group h-11 w-11 rounded-full border-2 border-gold/50 bg-gradient-to-br from-background/80 to-card/60 backdrop-blur-md hover:from-gold/30 hover:to-gold/20 hover:border-gold hover:scale-110 hover:shadow-gold-glow transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none pointer-events-auto shadow-lg"
                >
                  <ChevronLeft className="h-5 w-5 text-gold transition-transform duration-300 group-hover:-translate-x-0.5" />
                </Button>

                {/* Page divider line with ornaments */}
                <div className="flex flex-col items-center gap-4 flex-1 max-h-48">
                  <div className="w-px flex-1 bg-gradient-to-b from-transparent via-gold/50 to-transparent" />
                  <div className="w-2 h-2 rounded-full bg-gold/40 shadow-gold-glow"></div>
                  <div className="w-px flex-1 bg-gradient-to-b from-transparent via-gold/50 to-transparent" />
                </div>

                {/* Next Page Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={nextSpread}
                  disabled={currentSpread === totalSpreads - 1}
                  className="group h-11 w-11 rounded-full border-2 border-gold/50 bg-gradient-to-br from-background/80 to-card/60 backdrop-blur-md hover:from-gold/30 hover:to-gold/20 hover:border-gold hover:scale-110 hover:shadow-gold-glow transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none pointer-events-auto shadow-lg"
                >
                  <ChevronRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-0.5" />
                </Button>
                
                {/* Decorative bottom ornament */}
                <div className="w-6 h-6 opacity-30">
                  <svg viewBox="0 0 100 100" className="text-gold">
                    <circle cx="50" cy="50" r="3" fill="currentColor"/>
                    <circle cx="50" cy="30" r="2" fill="currentColor" opacity="0.6"/>
                    <circle cx="50" cy="70" r="2" fill="currentColor" opacity="0.6"/>
                  </svg>
                </div>
              </div>

              {/* Right Page - Desktop only */}
              <div className="hidden lg:flex relative flex-1 pointer-events-none h-full">
                <MenuBookPage pageNumber={currentSpread * 2 + 2}>
                  {pages[currentSpread].right}
                </MenuBookPage>
              </div>
            </div>

            {/* Mobile/Tablet Navigation - Bottom */}
            <div className="flex lg:hidden absolute bottom-4 left-1/2 -translate-x-1/2 gap-3 items-center justify-center z-50 pointer-events-auto">
              <Button
                variant="ghost"
                size="sm"
                onClick={prevMobilePage}
                disabled={currentSpread === 0 && currentMobilePage === 'left'}
                className="h-9 w-9 rounded-full border-2 border-gold/50 bg-background/80 backdrop-blur-md hover:bg-gold/20 hover:border-gold disabled:opacity-0 disabled:pointer-events-none shadow-lg"
              >
                <ChevronLeft className="h-4 w-4 text-gold" />
              </Button>
              
              <div className="px-4 py-2 rounded-full bg-background/80 backdrop-blur-md border-2 border-gold/30 shadow-lg min-w-[90px] text-center">
                <span className="text-gold/90 font-display text-xs tracking-wider font-semibold">
                  {currentSpread * 2 + (currentMobilePage === 'left' ? 1 : 2)} / {totalSpreads * 2}
                </span>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={nextMobilePage}
                disabled={currentSpread === totalSpreads - 1 && currentMobilePage === 'right'}
                className="h-9 w-9 rounded-full border-2 border-gold/50 bg-background/80 backdrop-blur-md hover:bg-gold/20 hover:border-gold disabled:opacity-0 disabled:pointer-events-none shadow-lg"
              >
                <ChevronRight className="h-4 w-4 text-gold" />
              </Button>
            </div>

            {/* Desktop Page Counter - Bottom Center */}
            <div className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-40 px-8 py-3 bg-gradient-to-r from-background/80 via-card/70 to-background/80 backdrop-blur-md border-2 border-gold/30 rounded-full shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-gold/60"></div>
                <p className="text-sm text-gold/90 font-display tracking-widest font-semibold">
                  Sida {currentSpread * 2 + 1}-{currentSpread * 2 + 2} <span className="text-gold/50 mx-1">·</span> {totalSpreads * 2}
                </p>
                <div className="w-1.5 h-1.5 rounded-full bg-gold/60"></div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DineInMenuDialog;
